import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {CdkDrag, CdkDragDrop, CdkDropList} from '@angular/cdk/drag-drop';
import {TesteDoLead} from './links-teste-gestor.component';

export interface LeadDoSiteRegistrado {
  id: number;
  telefone: string;
  nome: string;
  empresa: string;
  instagram: string | null;
  email: string | null;
  temComputador: boolean | null;
  origem: string;
  canal: string | null;
  urlPagina: string | null;
  ultimoFormularioEm: string;
  primeiraMensagemEm: string | null;
  testeCriadoEm: string | null;
  /** primeiro login no painel depois de criar o teste (usou o teste) */
  testeAcessadoEm: string | null;
  /** quando alguém chamou a Mia para iniciar a conversa (envio proativo) */
  miaChamadaEm: string | null;
  /** coluna em que o lead aparece */
  etapa: string;
  /** coluna mais à esquerda que os fatos permitem (escreveu → CONVERSA, criou o teste → TESTE, entrou no painel → TESTE_ATIVO) */
  etapaMinima: string;
  /** quem está devendo resposta; null em NOVO, CLIENTE e PERDIDO */
  situacaoConversa: SituacaoDoLead | null;
  /** por que foi marcado como Perdido; null fora de PERDIDO */
  motivoPerda: string | null;
}

/** Situação da conversa (eixo independente da etapa): espelha situacaoDoLead do servidor. */
export interface SituacaoDoLead {
  codigo: 'RESPONDENDO' | 'AGUARDANDO_LEAD' | 'FOLLOWUP' | 'ESFRIOU' | 'PRECISA_HUMANO' | 'PEDIU_PARAR' | string;
  passo?: number;
  minutos: number | null;
}

/** Rótulo, ícone e tom de cada situação. */
export const SITUACOES: Record<string, {rotulo: string; icone: string; tom: string; dica: string}> = {
  RESPONDENDO: {rotulo: 'Respondendo', icone: 'fa-comments', tom: 'ok', dica: 'O lead falou por último ou a conversa está quente'},
  AGUARDANDO_LEAD: {rotulo: 'Aguardando lead', icone: 'fa-hourglass-half', tom: 'neutro', dica: 'A Mia perguntou e o lead ainda não voltou'},
  FOLLOWUP: {rotulo: 'Follow-up', icone: 'fa-redo-alt', tom: 'atencao', dica: 'Toque automático enviado, lead sem responder; o próximo já está agendado'},
  ESFRIOU: {rotulo: 'Esfriou', icone: 'fa-snowflake', tom: 'frio', dica: 'Último toque sem resposta ou sete dias de silêncio. Não vira Perdido sozinho'},
  PRECISA_HUMANO: {rotulo: 'Precisa de humano', icone: 'fa-hand-paper', tom: 'alerta', dica: 'O lead pediu a equipe ou a Mia encaminhou'},
  PEDIU_PARAR: {rotulo: 'Pediu para parar', icone: 'fa-ban', tom: 'alerta', dica: 'O lead pediu para não receber mais mensagens'},
};

export function rotuloDaSituacao(s: SituacaoDoLead | null): string {
  if (!s) return '';
  const base = SITUACOES[s.codigo]?.rotulo || s.codigo;
  return s.codigo === 'FOLLOWUP' && s.passo ? `${base} ${['D1', 'D3', 'D7'][s.passo - 1] || s.passo}` : base;
}

/** Um fato da ficha comercial: o que a Mia registrou, com o trecho do lead que prova. */
interface FatoDaFicha { valor: string; evidencia?: string; turnoId?: number; registradoEm?: string; }

/** Memória da Mia Gestor sobre o lead: atendimento comercial com o mesmo telefone. */
export interface AtendimentoDoLead {
  atendimentoId: number;
  fase: string;
  ficha: {fatos?: Record<string, FatoDaFicha>; instagram?: {handle?: string; perfil?: any; motivo?: string; consultadoEm?: string}};
  encaminhamento: {motivo?: string} | null;
  mensagens: {id: number; papel: string; texto: string; criadoEm: string}[];
  controle: {habilitada?: boolean; bloqueada?: boolean; pausada?: boolean} | null;
  /** links que a Mia mandou ao criar o teste e se o lead abriu; null antes do teste */
  teste?: TesteDoLead | null;
}

/** Campos da ficha comercial, na ordem do SPIN (espelha CAMPOS_DA_FICHA do gestor-ia). */
export const CAMPOS_DA_FICHA: [string, string][] = [
  ['restaurante', 'Restaurante'], ['segmento', 'Segmento'], ['operacao', 'Operação'], ['canais', 'Canais de pedido'],
  ['ferramenta_atual', 'Como anota hoje'], ['instagram', 'Instagram'], ['cidade', 'Cidade'], ['volume_pedidos', 'Volume de pedidos'],
  ['equipamento', 'Equipamento'], ['problema', 'Problema'], ['impacto', 'Impacto'], ['necessidade', 'Necessidade'],
  ['objecao', 'Objeção'], ['intencao', 'Intenção'], ['proximo_passo', 'Próximo passo'],
  ['responsavel', 'Responsável'], ['email', 'E-mail'], ['whatsapp', 'WhatsApp da loja'],
];
/** O que a Mia precisa saber antes de oferecer o teste: situação da loja + dor (SPIN). */
const CAMPOS_DO_SPIN = ['restaurante', 'instagram', 'segmento', 'operacao', 'canais', 'ferramenta_atual', 'problema', 'impacto', 'necessidade'];

/** Mensagens já trocadas com o contato pelo número da Gestor (equipe, lead e Mia). */
export interface HistoricoDoContato { mensagens: number; ultimaEm: string | null; }

/** Um lead do e-mail colado, depois de gravado: o que aconteceu e o que dá para fazer com ele. */
export interface LeadColado extends LeadDoSiteRegistrado {
  situacao: 'novo' | 'atualizado' | 'mantido' | 'repetido';
  /** mensagens recentes do lead reentregues à Mia: ela responde sozinha */
  acordados: number;
  historico: HistoricoDoContato | null;
  miaJaFalou: boolean;
}

export interface ResultadoColagem {
  registrados: number;
  duplicados: number;
  acordados: number;
  rejeitados: string[];
  leads: LeadColado[];
}

/** Antes de gravar: telefone que já existe com dados diferentes pede Atualizar/Manter. */
export interface LinhaDaAnalise {
  telefone: string;
  nome: string;
  empresa: string;
  situacao: 'novo' | 'existente' | 'repetido';
  atual?: {id: number; nome: string; empresa: string};
  mudou?: boolean;
}

/** Acompanhamento de "Chamar a Mia" numa linha do resultado: espera a primeira mensagem dela. */
interface ChamadaDaMia {
  estado: 'chamando' | 'aguardando' | 'enviada' | 'demorou' | 'erro';
  texto?: string;
  erro?: string;
}

interface ColunaKanban {
  id: string;
  titulo: string;
  dica: string;
  vazio: string;
}

/** Ordem do funil; PERDIDO é saída e aceita lead de qualquer coluna. Espelha ETAPAS_DO_LEAD do servidor. */
const COLUNAS: ColunaKanban[] = [
  {id: 'NOVO', titulo: 'Novo', dica: 'Registrado, ainda não escreveu', vazio: 'Cole o e-mail de um lead para ele aparecer aqui.'},
  {id: 'CONVERSA', titulo: 'Em conversa', dica: 'Escreveu e está com a Mia', vazio: 'Quando o lead escrever, ele vem para cá sozinho.'},
  {id: 'TESTE', titulo: 'Teste criado', dica: 'Criou o teste de 7 dias, ainda não entrou', vazio: 'Quem criar o teste chega aqui sozinho.'},
  {id: 'TESTE_ATIVO', titulo: 'Teste ativo', dica: 'Entrou no painel depois de criar o teste', vazio: 'Quem entrar no painel do teste chega aqui sozinho.'},
  {id: 'CLIENTE', titulo: 'Cliente', dica: 'Fechou o plano', vazio: 'Arraste para cá quem fechou.'},
  {id: 'PERDIDO', titulo: 'Perdido', dica: 'Desistiu ou não responde', vazio: 'Arraste para cá quem desistiu.'},
];

const AVATARES = [
  ['#e8eefb', '#2d5cb8'], ['#e5f5ec', '#16875a'], ['#fff1e0', '#b45309'],
  ['#f1ebfb', '#6d3fc0'], ['#e3f4f7', '#0f7285'], ['#fbe9ef', '#b4235a'],
];

/**
 * Kanban dos leads do site: colar o e-mail do lead autoriza o telefone a ser atendido pela Mia Gestor
 * pelo número pessoal (extensão V4); arrastar o cartão muda a etapa. Só o superadmin vê.
 */
@Component({
  selector: 'app-leads-mia-gestor',
  templateUrl: './leads-mia-gestor.component.html',
  styleUrls: ['./leads-mia-gestor.component.scss'],
})
export class LeadsMiaGestorComponent implements OnInit, OnDestroy {
  readonly colunas = COLUNAS;
  readonly periodos = [7, 30, 90];

  leads: LeadDoSiteRegistrado[] = [];
  porEtapa: Record<string, LeadDoSiteRegistrado[]> = {};
  diasLeads = 30;
  busca = '';
  carregando = false;
  carregou = false;
  erro = '';

  colarAberto = false;
  emailColado = '';
  colando = false;
  colagem: ResultadoColagem | null = null;
  erroColagem = '';
  /** leads já registrados com dados diferentes no e-mail colado: confirmar antes de gravar */
  confirmacoes: LinhaDaAnalise[] = [];
  escolhas: Record<string, 'atualizar' | 'manter'> = {};
  chamadas: Record<number, ChamadaDaMia> = {};
  chamandoNaGaveta = false;
  private timersChamada: any[] = [];

  selecionado: LeadDoSiteRegistrado | null = null;
  /** o que a gaveta mostra; sobrevive ao fechar para a saída animar com conteúdo */
  exibido: LeadDoSiteRegistrado | null = null;
  arrastando: LeadDoSiteRegistrado | null = null;
  aviso: {texto: string; desfazer?: () => void} | null = null;
  copiado = false;
  confirmandoExclusao = false;
  memoria: AtendimentoDoLead | null = null;
  /** fato da memória com o trecho do lead aberto (rótulo) */
  fatoAberto: string | null = null;
  historico: HistoricoDoContato | null = null;
  miaJaFalou = false;
  carregandoMemoria = false;
  erroMemoria = '';
  private versaoMemoria = 0;
  excluindo = false;
  atualizandoAberto = false;
  emailDoLead = '';
  /** motivo da perda em edição na gaveta (lead em PERDIDO) */
  motivoPerda = '';
  salvandoMotivo = false;
  atualizando = false;
  erroAtualizacao = '';
  agora = Date.now();

  private relogio: any;
  private timerAviso: any;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.carregue();
    this.relogio = setInterval(() => this.agora = Date.now(), 60000);
  }

  ngOnDestroy() {
    clearInterval(this.relogio);
    clearTimeout(this.timerAviso);
    this.timersChamada.forEach(t => clearTimeout(t));
  }

  carregue() {
    this.carregando = true; this.erro = '';
    this.http.get<{leads: LeadDoSiteRegistrado[]}>(`/gestor-ia/leads-site?dias=${this.diasLeads}`).subscribe({
      next: r => {
        this.carregando = false;
        this.leads = r.leads || [];
        if (!this.carregou && !this.leads.length) this.colarAberto = true;
        this.carregou = true;
        if (this.selecionado) this.selecionado = this.exibido = this.leads.find(l => l.id === this.selecionado.id) || null;
        this.distribua();
      },
      error: e => { this.carregando = false; this.erro = e?.error?.erro || 'Não foi possível carregar os leads.'; },
    });
  }

  mudePeriodo(dias: number) {
    if (dias === this.diasLeads) return;
    this.diasLeads = dias;
    this.carregue();
  }

  distribua() {
    const termo = this.achate(this.busca);
    const digitos = this.busca.replace(/\D/g, '');
    const porEtapa: Record<string, LeadDoSiteRegistrado[]> = {};
    for (const c of COLUNAS) porEtapa[c.id] = [];
    for (const l of this.leads) {
      if (termo && !this.achate([l.nome, l.empresa, l.email, l.instagram].join(' ')).includes(termo)
          && !(digitos.length >= 3 && l.telefone.includes(digitos))) continue;
      (porEtapa[l.etapa] || porEtapa.NOVO).push(l);
    }
    this.porEtapa = porEtapa;
  }

  get totalVisivel(): number {
    return COLUNAS.reduce((n, c) => n + (this.porEtapa[c.id]?.length || 0), 0);
  }

  // ===== arrastar =====

  podeIr(lead: LeadDoSiteRegistrado, etapa: string): boolean {
    if (etapa === 'PERDIDO') return true;
    return this.indice(etapa) >= this.indice(lead.etapaMinima);
  }

  /** Coluna só aceita o cartão se não voltar para antes do que o lead já fez. */
  podeEntrar = (drag: CdkDrag<LeadDoSiteRegistrado>, lista: CdkDropList<string>) => this.podeIr(drag.data, lista.data);

  solte(evento: CdkDragDrop<string>) {
    if (evento.previousContainer === evento.container) return;
    this.mova(evento.item.data, evento.container.data);
  }

  mova(lead: LeadDoSiteRegistrado, etapa: string, comDesfazer = true) {
    if (lead.etapa === etapa || !this.podeIr(lead, etapa)) return;
    const anterior = lead.etapa;
    lead.etapa = etapa;
    this.distribua();
    this.http.put<{lead: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${lead.id}/etapa`, {etapa}).subscribe({
      next: r => {
        Object.assign(lead, r.lead);
        this.distribua();
        this.avise(`${this.nomeDe(lead)} → ${this.tituloDe(lead.etapa)}`,
          comDesfazer ? () => this.mova(lead, anterior, false) : undefined);
      },
      error: e => {
        lead.etapa = anterior;
        this.distribua();
        this.avise(e?.error?.erro || `Não foi possível mover ${this.nomeDe(lead)}.`);
      },
    });
  }

  /** Anota (ou corrige) o motivo de um lead já em PERDIDO; a etapa não muda. */
  salveMotivoPerda(lead: LeadDoSiteRegistrado) {
    if (this.salvandoMotivo || lead.etapa !== 'PERDIDO') return;
    this.salvandoMotivo = true;
    this.http.put<{lead: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${lead.id}/etapa`, {etapa: 'PERDIDO', motivo: this.motivoPerda.trim()}).subscribe({
      next: r => { this.salvandoMotivo = false; Object.assign(lead, r.lead); this.motivoPerda = lead.motivoPerda || ''; this.avise('Motivo da perda anotado.'); },
      error: e => { this.salvandoMotivo = false; this.avise(e?.error?.erro || 'Não foi possível anotar o motivo.'); },
    });
  }

  // ===== detalhe =====

  abra(lead: LeadDoSiteRegistrado) {
    this.selecionado = this.exibido = lead;
    this.copiado = false;
    this.confirmandoExclusao = false;
    this.atualizandoAberto = false; this.emailDoLead = ''; this.erroAtualizacao = '';
    this.motivoPerda = lead.motivoPerda || ''; this.salvandoMotivo = false;
    this.carregueMemoria(lead);
  }

  /** Colar o e-mail do formulário completa a ficha deste lead (nome, empresa, Instagram...); o telefone não muda. */
  atualizeComEmail(lead: LeadDoSiteRegistrado) {
    if (!this.emailDoLead.trim() || this.atualizando) return;
    this.atualizando = true; this.erroAtualizacao = '';
    this.http.put<{lead: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${lead.id}/dados-do-email`, {texto: this.emailDoLead}).subscribe({
      next: r => {
        this.atualizando = false;
        Object.assign(lead, r.lead);
        this.distribua();
        this.atualizandoAberto = false; this.emailDoLead = '';
        this.avise(`Dados de ${this.nomeDe(lead)} atualizados.`);
      },
      error: e => { this.atualizando = false; this.erroAtualizacao = e?.error?.erro || 'Não foi possível atualizar o lead.'; },
    });
  }

  private carregueMemoria(lead: LeadDoSiteRegistrado) {
    const versao = ++this.versaoMemoria;
    this.memoria = null; this.historico = null; this.miaJaFalou = false; this.erroMemoria = ''; this.carregandoMemoria = true; this.fatoAberto = null;
    this.http.get<{atendimento: AtendimentoDoLead | null; historico: HistoricoDoContato | null; miaJaFalou: boolean; lead?: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${lead.id}/atendimento`).subscribe({
      next: r => {
        if (versao !== this.versaoMemoria) return;
        this.carregandoMemoria = false; this.memoria = r.atendimento; this.historico = r.historico || null; this.miaJaFalou = !!r.miaJaFalou;
        if (lead.etapa === 'PERDIDO' && !lead.motivoPerda && !this.motivoPerda) this.motivoPerda = this.objecaoDe(this.memoria);
        // a lista não se recarrega sozinha: a etapa e a empresa do cartão vêm do lead atual
        if (r.lead) { Object.assign(lead, r.lead); this.distribua(); }
      },
      error: e => {
        if (versao !== this.versaoMemoria) return;
        this.carregandoMemoria = false;
        this.erroMemoria = e?.error?.erro || 'Não foi possível abrir a memória da Mia.';
      },
    });
  }

  /** Fatos da ficha na ordem do SPIN; campo desconhecido vai para o fim com o nome cru. */
  /** Objeção registrada pela Mia na ficha: sugestão de motivo da perda (a marcação continua humana). */
  objecaoDe(m: AtendimentoDoLead | null): string { return String(m?.ficha?.fatos?.objecao?.valor || '').trim(); }

  motivoSugerido(lead: LeadDoSiteRegistrado): boolean {
    const o = this.objecaoDe(this.memoria);
    return !lead.motivoPerda && !!o && this.motivoPerda === o;
  }

  /** O trecho só aparece quando acrescenta algo ao valor (a Mia às vezes grava o próprio texto do lead). */
  temTrecho(f: FatoDaFicha): boolean {
    return !!f.evidencia && f.evidencia.trim().toLocaleLowerCase() !== String(f.valor || '').trim().toLocaleLowerCase();
  }

  rastreieFato(_: number, f: {rotulo: string}) { return f.rotulo; }

  /** Perfis consultados antes da correção no servidor guardaram entidades HTML cruas ("Doc&#x327;uras"). */
  textoDoInstagram(texto: string | null | undefined): string {
    return String(texto || '').replace(/&#(x[0-9a-f]+|\d+);/gi, (inteira, n: string) => {
      const codigo = n[0] === 'x' || n[0] === 'X' ? parseInt(n.slice(1), 16) : Number(n);
      return codigo > 0 && codigo <= 0x10ffff ? String.fromCodePoint(codigo) : inteira;
    }).replace(/&amp;/g, '&').replace(/&quot;/g, '"').normalize('NFC');
  }

  fatosDe(m: AtendimentoDoLead): {rotulo: string; fato: FatoDaFicha}[] {
    const fatos = m?.ficha?.fatos || {};
    const conhecidos = CAMPOS_DA_FICHA.filter(([c]) => fatos[c]?.valor).map(([c, rotulo]) => ({rotulo, fato: fatos[c]}));
    const outros = Object.keys(fatos).filter(c => fatos[c]?.valor && !CAMPOS_DA_FICHA.some(([k]) => k === c))
      .map(c => ({rotulo: c.replace(/_/g, ' '), fato: fatos[c]}));
    return [...conhecidos, ...outros];
  }

  spinDe(m: AtendimentoDoLead): {feitos: boolean[]; falta: string[]} {
    const fatos = m?.ficha?.fatos || {};
    const feitos = CAMPOS_DO_SPIN.map(c => !!fatos[c]?.valor);
    const falta = CAMPOS_DO_SPIN.filter((_, i) => !feitos[i]).map(c => CAMPOS_DA_FICHA.find(([k]) => k === c)[1].toLowerCase());
    return {feitos, falta};
  }

  rotuloFase(fase: string): string {
    return ({INICIO: 'Início', SDR: 'Conversa comercial', CRIAR_TESTE: 'Criando o teste', AUTENTICADO: 'Teste criado'})[fase] || fase || '—';
  }

  estadoDaMia(m: AtendimentoDoLead): string {
    if (m.controle?.bloqueada) return 'Mia desativada';
    if (m.controle?.pausada) return 'Com a equipe';
    if (m.controle?.habilitada === false) return 'Mia desligada';
    return 'Mia atendendo';
  }

  hostDe(url: string): string {
    try { return new URL(url).host.replace(/^www\./, ''); } catch { return url; }
  }

  feche() { this.selecionado = null; }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (this.selecionado) this.feche();
    else if (this.colarAberto && !this.colando) this.colarAberto = false;
  }

  copieTelefone(lead: LeadDoSiteRegistrado) {
    navigator.clipboard?.writeText(lead.telefone).then(() => {
      this.copiado = true;
      setTimeout(() => this.copiado = false, 1600);
    });
  }

  /** Excluir tira o telefone da lista de autorizados: a Mia Gestor para de responder a ele. */
  exclua(lead: LeadDoSiteRegistrado) {
    if (!this.confirmandoExclusao) { this.confirmandoExclusao = true; return; }
    if (this.excluindo) return;
    this.excluindo = true;
    this.http.delete(`/gestor-ia/leads-site/${lead.id}`).subscribe({
      next: () => {
        this.excluindo = false;
        this.confirmandoExclusao = false;
        this.leads = this.leads.filter(l => l.id !== lead.id);
        this.distribua();
        this.feche();
        this.avise(`${this.nomeDe(lead)} foi excluído.`);
      },
      error: e => {
        this.excluindo = false;
        this.avise(e?.error?.erro || `Não foi possível excluir ${this.nomeDe(lead)}.`);
      },
    });
  }

  // ===== colar e-mail =====

  /** Primeiro analisa: telefone já registrado com dados diferentes pede Atualizar/Manter; sem isso, grava direto. */
  coleEmail() {
    if (!this.emailColado.trim() || this.colando) return;
    this.colando = true; this.colagem = null; this.erroColagem = ''; this.confirmacoes = []; this.escolhas = {}; this.chamadas = {};
    this.http.post<{linhas: LinhaDaAnalise[]; rejeitados: string[]}>('/gestor-ia/leads-site/analisar', {texto: this.emailColado}).subscribe({
      next: r => {
        const pendentes = r.linhas.filter(l => l.situacao === 'existente' && l.mudou);
        if (!pendentes.length) { this.grave(); return; }
        this.colando = false;
        this.confirmacoes = pendentes;
        for (const p of pendentes) this.escolhas[p.telefone] = 'atualizar';
      },
      error: e => { this.colando = false; this.erroColagem = e?.error?.erro || 'Não foi possível ler o e-mail.'; },
    });
  }

  /** Grava tudo com as escolhas feitas (novos entram direto). */
  grave() {
    this.colando = true; this.erroColagem = '';
    this.http.post<ResultadoColagem>('/gestor-ia/leads-site/colar', {texto: this.emailColado, escolhas: this.escolhas}).subscribe({
      next: r => {
        this.colando = false;
        this.colagem = r;
        this.confirmacoes = [];
        if (r.leads?.length) { this.emailColado = ''; this.carregue(); }
      },
      error: e => { this.colando = false; this.erroColagem = e?.error?.erro || 'Não foi possível registrar.'; },
    });
  }

  canceleConfirmacao() {
    this.confirmacoes = []; this.escolhas = {};
  }

  rotuloSituacao(situacao: LeadColado['situacao']): string {
    return ({novo: 'Novo', atualizado: 'Dados atualizados', mantido: 'Já registrado', repetido: 'Já registrado'})[situacao] || situacao;
  }

  /** Lead que acordou mensagens não precisa de chamada: a Mia já vai responder. */
  podeChamar(l: LeadColado): boolean {
    return !l.acordados && !l.miaJaFalou && !l.miaChamadaEm;
  }

  historicoTexto(h: HistoricoDoContato | null): string {
    if (!h || !h.mensagens) return '';
    const n = h.mensagens === 1 ? '1 mensagem' : `${h.mensagens} mensagens`;
    if (!h.ultimaEm) return n;
    const d = new Date(h.ultimaEm);
    const dois = (v: number) => String(v).padStart(2, '0');
    return `${n}, última em ${dois(d.getDate())}/${dois(d.getMonth() + 1)}`;
  }

  // ===== chamar a Mia =====

  /** Da lista do colar: chama e fica esperando a primeira mensagem dela para mostrar na linha. */
  chameMiaNaLista(l: LeadColado) {
    if (this.chamadas[l.id] && this.chamadas[l.id].estado !== 'erro') return;
    this.chamadas[l.id] = {estado: 'chamando'};
    this.http.post<{ok: boolean; retomada: boolean; lead: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${l.id}/chamar-mia`, {}).subscribe({
      next: r => {
        Object.assign(l, r.lead);
        const noQuadro = this.leads.find(x => x.id === l.id);
        if (noQuadro) Object.assign(noQuadro, r.lead);
        this.chamadas[l.id] = {estado: 'aguardando'};
        this.espereMensagemDaMia(l.id, Date.now());
      },
      error: e => { this.chamadas[l.id] = {estado: 'erro', erro: e?.error?.erro || 'Não foi possível chamar a Mia.'}; },
    });
  }

  /** Consulta a memória a cada 5 s, por até 90 s, até a primeira mensagem da Mia depois da chamada. */
  private espereMensagemDaMia(id: number, desde: number) {
    const consulte = () => {
      this.http.get<{atendimento: AtendimentoDoLead | null}>(`/gestor-ia/leads-site/${id}/atendimento`).subscribe({
        next: r => {
          const msgs = (r.atendimento?.mensagens || []).filter(m => m.papel === 'gestor' && new Date(m.criadoEm).getTime() >= desde - 60000);
          if (msgs.length) { this.chamadas[id] = {estado: 'enviada', texto: msgs[msgs.length - 1].texto}; return; }
          if (Date.now() - desde > 90000) { this.chamadas[id] = {estado: 'demorou'}; return; }
          this.timersChamada.push(setTimeout(consulte, 5000));
        },
        error: () => { this.timersChamada.push(setTimeout(consulte, 5000)); },
      });
    };
    this.timersChamada.push(setTimeout(consulte, 5000));
  }

  iniciandoTesteNaGaveta = false;

  /** Cabe enquanto a Mia ainda qualifica: sem teste criado e fora de CRIAR_TESTE. */
  get podeIniciarTesteNaGaveta(): boolean {
    const fase = this.memoria?.fase;
    return !this.carregandoMemoria && !this.exibido?.testeCriadoEm && fase !== 'CRIAR_TESTE' && fase !== 'AUTENTICADO';
  }

  /** Da gaveta: a equipe decide pelo lead; a fase vira CRIAR_TESTE e a Mia pede só o que falta. */
  inicieTesteNaGaveta(lead: LeadDoSiteRegistrado) {
    if (this.iniciandoTesteNaGaveta) return;
    this.iniciandoTesteNaGaveta = true;
    this.http.post<{ok: boolean; lead: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${lead.id}/iniciar-teste`, {}).subscribe({
      next: r => {
        this.iniciandoTesteNaGaveta = false;
        Object.assign(lead, r.lead);
        this.distribua();
        this.avise('A Mia vai começar a criação do teste: ela pede só o que falta.');
        this.timersChamada.push(setTimeout(() => { if (this.selecionado?.id === lead.id) this.carregueMemoria(lead); }, 5000));
      },
      error: e => { this.iniciandoTesteNaGaveta = false; this.avise(e?.error?.erro || 'Não foi possível iniciar o teste.'); },
    });
  }

  /** Da gaveta: chama e recarrega a memória em 5 s para mostrar o que ela mandou. */
  chameMiaNaGaveta(lead: LeadDoSiteRegistrado) {
    if (this.chamandoNaGaveta) return;
    this.chamandoNaGaveta = true;
    this.http.post<{ok: boolean; retomada: boolean; lead: LeadDoSiteRegistrado}>(`/gestor-ia/leads-site/${lead.id}/chamar-mia`, {}).subscribe({
      next: r => {
        this.chamandoNaGaveta = false;
        Object.assign(lead, r.lead);
        this.distribua();
        this.avise('A Mia entrou na conversa. A primeira mensagem sai em instantes.');
        this.timersChamada.push(setTimeout(() => { if (this.selecionado?.id === lead.id) this.carregueMemoria(lead); }, 5000));
      },
      error: e => { this.chamandoNaGaveta = false; this.avise(e?.error?.erro || 'Não foi possível chamar a Mia.'); },
    });
  }

  /** Chamar a Mia da gaveta: só quando ela ainda não está na conversa. */
  get podeChamarNaGaveta(): boolean {
    return !this.carregandoMemoria && !this.miaJaFalou && !this.exibido?.miaChamadaEm;
  }

  motivoNaoChamar(): string {
    if (this.miaJaFalou) return 'A Mia já está na conversa';
    if (this.exibido?.miaChamadaEm) return 'Mia chamada';
    return '';
  }

  // ===== apresentação =====

  tituloDe(etapa: string): string {
    return COLUNAS.find(c => c.id === etapa)?.titulo || etapa;
  }

  motivoBloqueio(lead: LeadDoSiteRegistrado): string {
    if (lead.etapaMinima === 'TESTE_ATIVO') return 'Já entrou no painel do teste';
    return lead.etapaMinima === 'TESTE' ? 'Já criou o teste' : 'Já escreveu para a Mia';
  }

  situacaoDe(lead: LeadDoSiteRegistrado) {
    return lead.situacaoConversa ? SITUACOES[lead.situacaoConversa.codigo] || null : null;
  }

  rotuloSituacaoConversa(lead: LeadDoSiteRegistrado): string {
    return rotuloDaSituacao(lead.situacaoConversa);
  }

  /** "Aguardando lead · 2 h": o tempo vem em minutos do servidor, contado no relógio do banco. */
  tempoSituacao(lead: LeadDoSiteRegistrado): string {
    const m = lead.situacaoConversa?.minutos;
    return m === null || m === undefined ? '' : this.tempoDeMinutos(m);
  }

  nomeDe(lead: LeadDoSiteRegistrado): string {
    return lead.nome || lead.empresa || this.telefoneFormatado(lead.telefone);
  }

  iniciais(lead: LeadDoSiteRegistrado): string {
    const partes = (lead.nome || lead.empresa || '').trim().split(/\s+/).filter(Boolean);
    if (!partes.length) return '#';
    return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
  }

  corAvatar(lead: LeadDoSiteRegistrado): string[] {
    const soma = lead.telefone.split('').reduce((n, d) => n + Number(d || 0), 0);
    return AVATARES[soma % AVATARES.length];
  }

  telefoneFormatado(telefone: string): string {
    const m = /^(\d{2})(\d{5})(\d{4})$/.exec(telefone || '');
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : telefone;
  }

  linkWhatsapp(lead: LeadDoSiteRegistrado): string {
    return `https://wa.me/55${lead.telefone}`;
  }

  linkWhatsappWeb(lead: LeadDoSiteRegistrado): string {
    return `https://web.whatsapp.com/send?phone=55${lead.telefone}`;
  }

  linkInstagram(lead: LeadDoSiteRegistrado): string {
    return `https://instagram.com/${encodeURIComponent(lead.instagram.replace(/^@/, '').trim())}`;
  }

  tempoDesde(data: string | null): string {
    if (!data) return '';
    return this.tempoDeMinutos(Math.floor((this.agora - new Date(data).getTime()) / 60000));
  }

  tempoDeMinutos(valor: number): string {
    const minutos = Math.max(0, valor);
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `${minutos} min`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `${horas} h`;
    const dias = Math.floor(horas / 24);
    return dias === 1 ? 'ontem' : `${dias} d`;
  }

  rotuloOrigem(origem: string): string {
    return ({site_meucardapio: 'MeuCardápio', site_cardapiodigital: 'CardápioDigital', saudacao_site: 'Botão do site', manual: 'Manual'})[origem] || origem;
  }

  iconeCanal(canal: string): string {
    return ({instagram: 'fab fa-instagram', facebook: 'fab fa-facebook', google: 'fab fa-google'})[canal] || 'fas fa-globe';
  }

  rotuloCanal(canal: string): string {
    return ({instagram: 'Instagram', facebook: 'Facebook', google: 'Google', site: 'Site'})[canal] || canal;
  }

  porId(_: number, l: LeadDoSiteRegistrado) { return l.id; }
  porTelefone(_: number, l: LinhaDaAnalise) { return l.telefone; }

  private avise(texto: string, desfazer?: () => void) {
    clearTimeout(this.timerAviso);
    this.aviso = {texto, desfazer};
    this.timerAviso = setTimeout(() => this.aviso = null, 5000);
  }

  desfaca() {
    const desfazer = this.aviso?.desfazer;
    this.aviso = null;
    if (desfazer) desfazer();
  }

  private indice(etapa: string): number {
    return COLUNAS.findIndex(c => c.id === etapa);
  }

  private achate(texto: string): string {
    return String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }
}
